import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Boat, ChartLineUp, Plus, FloppyDisk } from "@phosphor-icons/react";

const field = "w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30";
const SHIP_STATUS = ["Order Confirmed", "In Production", "QC Passed", "Departed Port", "In Transit", "Arrived Port", "Delivered"];
const MODES = ["Sea FCL", "Sea LCL", "Air Freight", "Express"];
const STAGES = ["Supplier Selection", "Sampling", "Production", "QC & Inspection", "Shipping", "Completed"];
const EMPTY_SHIP = { client_email: "", title: "", mode: "Sea FCL", origin: "China", destination: "", status: "Order Confirmed", progress: 0, eta: "" };
const EMPTY_PROJ = { client_email: "", name: "", category: "", stage: "Supplier Selection", progress: 0, supplier: "" };

function ClientOptions({ clients }) {
  return (
    <>
      <option value="">Select client…</option>
      {clients.map((c) => <option key={c.id} value={c.email}>{`${c.name} (${c.email})`}</option>)}
    </>
  );
}

function ShipmentRow({ s, onSave }) {
  const [status, setStatus] = useState(s.status);
  const [progress, setProgress] = useState(s.progress);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4" data-testid={`admin-shipment-${s.id}`}>
      <div className="min-w-[180px]">
        <p className="font-heading text-sm font-semibold text-ink">{s.title}</p>
        <p className="font-mono text-xs text-slate-400">{s.tracking} · {s.client_email}</p>
        <p className="text-xs text-slate-500">{s.origin} → {s.destination}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid={`shipment-status-${s.id}`}>
          {SHIP_STATUS.map((x) => <option key={x}>{x}</option>)}
        </select>
        <input type="number" min="0" max="100" value={progress} onChange={(e) => setProgress(e.target.value)} className="w-16 rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid={`shipment-progress-${s.id}`} />
        <button onClick={() => onSave(s.id, { status, progress: Number(progress) })} data-testid={`shipment-save-${s.id}`} className="flex items-center gap-1 rounded-md bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-800"><FloppyDisk size={14} /> Save</button>
      </div>
    </div>
  );
}

function ProjectRow({ p, onSave }) {
  const [stage, setStage] = useState(p.stage);
  const [progress, setProgress] = useState(p.progress);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4" data-testid={`admin-project-${p.id}`}>
      <div className="min-w-[180px]">
        <p className="font-heading text-sm font-semibold text-ink">{p.name}</p>
        <p className="text-xs text-slate-400">{p.category} · {p.client_email}</p>
        <p className="text-xs text-slate-500">{p.supplier}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid={`project-stage-${p.id}`}>
          {STAGES.map((x) => <option key={x}>{x}</option>)}
        </select>
        <input type="number" min="0" max="100" value={progress} onChange={(e) => setProgress(e.target.value)} className="w-16 rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid={`project-progress-${p.id}`} />
        <button onClick={() => onSave(p.id, { stage, progress: Number(progress) })} data-testid={`project-save-${p.id}`} className="flex items-center gap-1 rounded-md bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-800"><FloppyDisk size={14} /> Save</button>
      </div>
    </div>
  );
}

function ShipmentForm({ ship, setShip, clients, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-shipment-form">
      <div className="flex items-center gap-2"><Boat size={20} weight="duotone" className="text-teal" /><h3 className="font-heading text-lg font-semibold text-ink">Assign a shipment</h3></div>
      <select required value={ship.client_email} onChange={(e) => setShip({ ...ship, client_email: e.target.value })} className={field} data-testid="shipment-client"><ClientOptions clients={clients} /></select>
      <input required placeholder="Shipment title (e.g. Apparel — 1x40HQ)" value={ship.title} onChange={(e) => setShip({ ...ship, title: e.target.value })} className={field} data-testid="shipment-title" />
      <div className="grid grid-cols-2 gap-3">
        <select value={ship.mode} onChange={(e) => setShip({ ...ship, mode: e.target.value })} className={field}>{MODES.map((m) => <option key={m}>{m}</option>)}</select>
        <select value={ship.status} onChange={(e) => setShip({ ...ship, status: e.target.value })} className={field}>{SHIP_STATUS.map((m) => <option key={m}>{m}</option>)}</select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input placeholder="Origin" value={ship.origin} onChange={(e) => setShip({ ...ship, origin: e.target.value })} className={field} />
        <input required placeholder="Destination" value={ship.destination} onChange={(e) => setShip({ ...ship, destination: e.target.value })} className={field} data-testid="shipment-destination" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input type="date" value={ship.eta} onChange={(e) => setShip({ ...ship, eta: e.target.value })} className={field} />
        <input type="number" min="0" max="100" placeholder="Progress %" value={ship.progress} onChange={(e) => setShip({ ...ship, progress: e.target.value })} className={field} />
      </div>
      <button type="submit" data-testid="shipment-create" className="flex items-center gap-2 rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800"><Plus size={16} weight="bold" /> Create shipment</button>
    </form>
  );
}

function ProjectForm({ proj, setProj, clients, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-project-form">
      <div className="flex items-center gap-2"><ChartLineUp size={20} weight="duotone" className="text-teal" /><h3 className="font-heading text-lg font-semibold text-ink">Assign a sourcing project</h3></div>
      <select required value={proj.client_email} onChange={(e) => setProj({ ...proj, client_email: e.target.value })} className={field} data-testid="project-client"><ClientOptions clients={clients} /></select>
      <input required placeholder="Project name" value={proj.name} onChange={(e) => setProj({ ...proj, name: e.target.value })} className={field} data-testid="project-name" />
      <input placeholder="Category (e.g. Electronics)" value={proj.category} onChange={(e) => setProj({ ...proj, category: e.target.value })} className={field} />
      <div className="grid grid-cols-2 gap-3">
        <select value={proj.stage} onChange={(e) => setProj({ ...proj, stage: e.target.value })} className={field}>{STAGES.map((m) => <option key={m}>{m}</option>)}</select>
        <input type="number" min="0" max="100" placeholder="Progress %" value={proj.progress} onChange={(e) => setProj({ ...proj, progress: e.target.value })} className={field} />
      </div>
      <input placeholder="Supplier / factory ref" value={proj.supplier} onChange={(e) => setProj({ ...proj, supplier: e.target.value })} className={field} />
      <button type="submit" data-testid="project-create" className="flex items-center gap-2 rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800"><Plus size={16} weight="bold" /> Create project</button>
    </form>
  );
}

export function ClientTracking() {
  const [clients, setClients] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [ship, setShip] = useState(EMPTY_SHIP);
  const [proj, setProj] = useState(EMPTY_PROJ);

  const load = useCallback(async () => {
    try {
      const [c, s, p] = await Promise.all([api.get("/admin/clients"), api.get("/admin/shipments"), api.get("/admin/sourcing-projects")]);
      setClients(c.data); setShipments(s.data); setProjects(p.data);
    } catch (e) { console.error("Failed to load client tracking data:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createShipment = async (e) => {
    e.preventDefault();
    if (!ship.client_email) return toast.error("Select a client first");
    try {
      const { data } = await api.post("/admin/shipments", { ...ship, progress: Number(ship.progress) });
      toast.success(`Shipment ${data.tracking} assigned`);
      setShip(EMPTY_SHIP);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const createProject = async (e) => {
    e.preventDefault();
    if (!proj.client_email) return toast.error("Select a client first");
    try {
      await api.post("/admin/sourcing-projects", { ...proj, progress: Number(proj.progress) });
      toast.success("Sourcing project assigned");
      setProj(EMPTY_PROJ);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveShipment = async (id, patch) => {
    try { await api.patch(`/admin/shipments/${id}`, patch); toast.success("Shipment updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  const saveProject = async (id, patch) => {
    try { await api.patch(`/admin/sourcing-projects/${id}`, patch); toast.success("Project updated"); load(); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-client-tracking">
      <div className="grid gap-6 lg:grid-cols-2">
        <ShipmentForm ship={ship} setShip={setShip} clients={clients} onSubmit={createShipment} />
        <ProjectForm proj={proj} setProj={setProj} clients={clients} onSubmit={createProject} />
      </div>

      <div>
        <h3 className="mb-3 font-heading text-base font-semibold text-ink">Active shipments ({shipments.length})</h3>
        <div className="space-y-3">
          {shipments.map((s) => <ShipmentRow key={s.id} s={s} onSave={saveShipment} />)}
          {shipments.length === 0 && <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-400">No shipments assigned yet.</p>}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-heading text-base font-semibold text-ink">Sourcing projects ({projects.length})</h3>
        <div className="space-y-3">
          {projects.map((p) => <ProjectRow key={p.id} p={p} onSave={saveProject} />)}
          {projects.length === 0 && <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-400">No projects assigned yet.</p>}
        </div>
      </div>
    </div>
  );
}
